/**
 * Conversation Mode Page
 *
 * Side-by-side view for manager-employee evaluation meetings.
 * Shows self-evaluation vs manager assessment for discussion.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEvaluation, useCalculateScores } from './useEvaluations';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { ScoreDisplay } from './wizard/ScoreInput';

export function ConversationModePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const evaluationId = parseInt(id || '', 10);

  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: assignedTasks, isLoading: isLoadingTasks } = useEmployeeAssignedTasks(
    evaluation?.employeeId || 0
  );
  const { data: scores } = useCalculateScores(evaluationId);

  if (isLoadingEval || isLoadingTasks) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Laden...</span>
        </div>
      </Layout>
    );
  }

  if (!evaluation) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">Beoordeling niet gevonden.</p>
            <button
              onClick={() => navigate('/team')}
              className="mt-4 text-blue-600 hover:text-blue-800"
            >
              Terug naar team overzicht
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const selfEvaluations = evaluation.selfEvaluations || [];
  const taskScores = evaluation.taskScores || [];

  return (
    <Layout>
      <div data-testid="conversation-mode-page" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Terug
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Gespreksmodus</h1>
            <p className="mt-1 text-sm text-gray-500">
              Vergelijk de zelf-evaluatie met de manager beoordeling tijdens het gesprek.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
              Live Meeting
            </span>
            <button
              onClick={() => navigate(`/beoordeling/${evaluationId}/pdf-preview`)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Export PDF
            </button>
            <button
              onClick={() => navigate(`/beoordeling/${evaluationId}/wizard`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Naar Wizard
            </button>
          </div>
        </div>

        {/* Score Summary */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <p className="text-sm text-yellow-700 mb-1">Zelf-score Gemiddelde</p>
            <p className="text-2xl font-bold text-yellow-900">
              {selfEvaluations.length > 0
                ? (selfEvaluations.reduce((sum, se) => sum + se.score, 0) / selfEvaluations.length).toFixed(2)
                : '—'}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-700 mb-1">Manager Score Gemiddelde</p>
            <p className="text-2xl font-bold text-blue-900">
              {taskScores.length > 0
                ? (taskScores.reduce((sum, ts) => sum + ts.score, 0) / taskScores.length).toFixed(2)
                : '—'}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-sm text-green-700 mb-1">Totaalscore</p>
            <p className="text-2xl font-bold text-green-900">
              {scores?.totalScore?.toFixed(2) || evaluation.totalScore || '—'}
            </p>
          </div>
        </div>

        {/* Side-by-side Comparison */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
            <div className="px-4 py-3 font-medium text-gray-700">Taak</div>
            <div className="px-4 py-3 font-medium text-yellow-700 text-center border-l border-gray-200">
              Zelf-evaluatie
            </div>
            <div className="px-4 py-3 font-medium text-blue-700 text-center border-l border-gray-200">
              Manager Beoordeling
            </div>
          </div>

          {assignedTasks?.tasks?.map((task) => {
            const selfEval = selfEvaluations.find((se) => se.taskId === task.id);
            const managerScore = taskScores.find((ts) => ts.taskId === task.id);
            const diff = selfEval && managerScore ? selfEval.score - managerScore.score : null;

            return (
              <div
                key={task.id}
                className={`grid grid-cols-3 border-b border-gray-200 last:border-b-0 ${
                  diff !== null && Math.abs(diff) >= 2 ? 'bg-red-50' : ''
                }`}
              >
                {/* Task info */}
                <div className="px-4 py-4">
                  <p className="font-medium text-gray-900">{task.name}</p>
                  <p className="text-sm text-gray-500">Weging: {task.weging}%</p>
                </div>

                {/* Self-evaluation */}
                <div className="px-4 py-4 border-l border-gray-200 text-center">
                  {selfEval ? (
                    <div>
                      <ScoreDisplay score={selfEval.score} size="md" />
                      {selfEval.toelichting && (
                        <p className="mt-2 text-sm text-gray-500 italic">
                          "{selfEval.toelichting}"
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Niet ingevuld</span>
                  )}
                </div>

                {/* Manager score */}
                <div className="px-4 py-4 border-l border-gray-200 text-center">
                  {managerScore ? (
                    <div>
                      <ScoreDisplay score={managerScore.score} size="md" />
                      {managerScore.toelichting && (
                        <p className="mt-2 text-sm text-gray-500 italic">
                          "{managerScore.toelichting}"
                        </p>
                      )}
                      {diff !== null && Math.abs(diff) >= 2 && (
                        <p className="mt-2 text-xs text-red-600 font-medium">
                          Verschil: {diff > 0 ? '+' : ''}{diff} punten
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">Niet ingevuld</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notes Section */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Gespreksnotities</h3>
          <textarea
            rows={4}
            placeholder="Maak aantekeningen tijdens het gesprek..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-2 text-xs text-gray-500">
            Notities worden niet automatisch opgeslagen. Kopieer belangrijke punten naar de afspraken in de wizard.
          </p>
        </div>

        {/* Agreements Preview */}
        {evaluation.agreements && evaluation.agreements.length > 0 && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Afspraken ({evaluation.agreements.length})
            </h3>
            <div className="space-y-3">
              {evaluation.agreements.map((agreement) => (
                <div
                  key={agreement.id}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                    {agreement.type}
                  </span>
                  <p className="text-sm text-gray-700">{agreement.beschrijving}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
